'use client'

import type { Partner } from '@/payload-types'
import { useEffect, useState } from 'react'
import { usePathname } from '@/i18n/routing'
import { z } from 'zod'
import { useWindowSize } from '@/website/hooks/useWindowSize'

const PropsSchema = z.object({
	partner: z.custom<Partner>(),
})

type Props = z.infer<typeof PropsSchema>

export const HeaderBg = (props: Props) => {
	const { partner } = props

	const [show, setShow] = useState(false)

	const pathname = usePathname()
	const isHomePage = pathname === '/'

	const windowSize = useWindowSize()

	const biggerSize = windowSize.width >= 640

	useEffect(() => {
		if (isHomePage) {
			document.body.classList.add('site-home')
			const siteHeader = document.querySelector('.site-header-logo-playdia')
			if (siteHeader) {
				siteHeader.classList.remove('hidden')		
			}
		}

		if (partner.slug === 'playdia') {
			setShow(true)
		}
		const header = document.querySelector('.header-main')
		if (header) {
			header.classList.remove('invisible')
		}

		return () => {
			if (isHomePage) {
				document.body.classList.remove('site-home')
				const siteHeaderLogo = document.querySelector('.site-header-logo-playdia')
				if (siteHeaderLogo) {
					siteHeaderLogo.classList.add('hidden')	
				}
	
				setShow(false)
			}
		}
	}, [pathname])

	return isHomePage && show ? (
		<div className="absolute top-0 left-0 -z-1 h-[153px] sm:h-[200px] lg:h-[270px] w-full overflow-hidden">
			{biggerSize ? (
				<svg
					viewBox="0 0 1440 268"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className="h-[200px] lg:h-[270px] w-full"
					preserveAspectRatio="none"
				>
					<mask
						id="mask0_19_463"
						style={{ maskType: 'alpha' }}
						maskUnits="userSpaceOnUse"
						x="0"
						y="0"
						width="1441"
						height="268"
					>
						<rect width="1441" height="268" fill="#D9D9D9" />
					</mask>
					<g mask="url(#mask0_19_463)">
						<mask
							id="mask1_19_463"
							style={{ maskType: 'luminance' }}
							maskUnits="userSpaceOnUse"
							x="0"
							y="0"
							width="1440"
							height="750"
						>
							<path d="M1440 750H0V0H1440V750Z" fill="white" />
						</mask>
						<g mask="url(#mask1_19_463)">
							<g clipPath="url(#clip0_19_463)">
								<path
									d="M73.2221 221.246C126.419 229.839 179.235 204.164 223.096 172.851C266.957 141.539 306.832 103.508 356.002 81.456C434.971 46.0387 527.128 56.7624 610.121 81.301C693.115 105.84 772.286 143.39 857.005 161.071C934.993 177.346 1016.54 183.218 1094.57 163.985C1165.8 146.432 1233.95 114.88 1307.95 110.082C1397.76 104.26 1487.59 116.451 1576.36 128.792C1569.48 10.2016 1559.35 -108.199 1545.96 -226.232C1545.49 -230.399 1544.88 -234.874 1542.03 -237.946C1538.33 -241.924 1532.24 -242.201 1526.81 -242.201C1185.88 -242.3 844.949 -242.399 504.02 -242.501C406.478 -242.529 308.534 -242.531 211.995 -228.572C161.742 -221.305 107.151 -207.161 78.2861 -165.381C60.2123 -139.221 55.3915 -106.374 51.9416 -74.7643C45.0396 -11.5193 73.0779 157.625 73.2221 221.246Z"
									fill="#F58968"
								/>
								<g style={{ mixBlendMode: 'multiply' }} opacity="0.2">
									<path
										d="M73.2221 133.751C126.419 142.344 179.235 116.669 223.096 85.3564C266.957 54.0439 306.832 16.0133 356.002 -6.03909C434.971 -41.4564 527.128 -30.7326 610.121 -6.19406C693.115 18.3445 772.286 55.895 857.005 73.5757C934.993 89.8508 1016.54 95.7228 1094.57 76.4902C1165.8 58.9365 1233.95 27.385 1307.95 22.5871C1397.76 16.7646 1487.59 28.9564 1576.36 41.2967C1569.48 -77.2935 1559.35 -195.694 1545.96 -313.727C1545.49 -317.894 1544.88 -322.369 1542.03 -325.441C1538.33 -329.419 1532.24 -329.696 1526.81 -329.696C1185.88 -329.796 844.949 -329.895 504.02 -329.996C406.478 -330.024 308.534 -330.026 211.995 -316.067C161.742 -308.8 107.151 -294.656 78.2861 -252.876C60.2123 -226.716 55.3915 -193.869 51.9416 -162.259C45.0396 -99.0145 73.0779 70.1295 73.2221 133.751Z"
										fill="#E8876A"
									/>
								</g>
								<path
									d="M318.348 -161.423C321.849 -110.873 347.236 -62.2157 386.696 -30.4361C426.155 1.34349 479.098 15.7782 529.224 8.41876C560.404 3.84038 589.781 -8.59466 619.451 -19.2259C713.926 -53.0784 816.771 -69.2674 915.296 -50.1832C1120.15 -10.5018 1280.18 173.804 1488.17 190.557C1510.48 192.355 1536.58 190.39 1549.55 172.143C1558.05 160.19 1558.38 144.466 1558.39 129.801C1558.44 36.382 1558.49 -57.039 1558.55 -150.458C1558.55 -158.138 1558.34 -166.468 1553.57 -172.482C1547.2 -180.498 1535.51 -181.215 1525.28 -181.26C1127.18 -182.946 716.317 -171.649 318.348 -161.423Z"
									fill="#EC5024"
								/>
								<g style={{ mixBlendMode: 'multiply' }} opacity="0.2">
									<path
										d="M389.799 -232.886C393.3 -182.336 418.687 -133.679 458.147 -101.899C497.607 -70.1197 550.549 -55.685 600.675 -63.0445C631.856 -67.6228 661.232 -80.0579 690.902 -90.6891C785.377 -124.542 888.222 -140.731 986.747 -121.646C1191.6 -81.965 1351.63 102.341 1559.62 119.094C1581.93 120.892 1608.03 118.926 1621 100.68C1629.5 88.7267 1629.83 73.0026 1629.84 58.3375C1629.89 -35.0813 1629.95 -128.502 1630 -221.921C1630 -229.601 1629.79 -237.931 1625.02 -243.945C1618.65 -251.961 1606.96 -252.678 1596.73 -252.723C1198.63 -254.409 787.768 -243.112 389.799 -232.886Z"
										fill="#B23A25"
									/>
								</g>
								<path
									d="M-30.4275 265.898C33.4139 272.549 99.7282 249.769 146.012 205.29C195.761 157.479 223.268 86.6158 282.586 51.3707C320.27 28.9782 366.001 24.3287 409.776 26.5738C453.55 28.8167 496.835 37.3859 540.624 39.2693C579.632 40.9461 618.866 37.2718 656.886 28.3819C707.134 16.6335 756.341 -5.02069 792.628 -41.7123C828.918 -78.4061 850.612 -131.713 841.217 -182.463C840.234 -187.771 838.835 -193.212 835.435 -197.403C828.322 -206.166 815.34 -206.599 804.061 -206.293C537.792 -199.104 271.524 -191.912 5.25516 -184.723C0.709814 -184.6 -4.16911 -184.365 -7.78042 -181.602C-12.8401 -177.729 -13.5224 -170.497 -13.7569 -164.128C-18.6402 -32.0347 -42.953 134.313 -30.4232 265.902"
									fill="#EC5024"
								/>
								<g style={{ mixBlendMode: 'multiply' }} opacity="0.2">
									<path
										d="M-104.453 191.861C-40.6116 198.512 25.7026 175.732 71.9868 131.252C121.736 83.4409 149.243 12.5782 208.56 -22.6669C246.244 -45.0595 291.975 -49.7089 335.75 -47.4638C379.525 -45.2209 422.809 -36.6518 466.599 -34.7683C505.607 -33.0915 544.84 -36.7658 582.86 -45.6557C633.109 -57.4041 682.315 -79.0583 718.603 -115.75C754.892 -152.444 776.586 -205.751 767.192 -256.5C766.208 -261.808 764.81 -267.25 761.409 -271.441C754.296 -280.204 741.315 -280.636 730.035 -280.331C463.767 -273.141 197.498 -265.95 -68.7704 -258.76C-73.3157 -258.638 -78.1946 -258.403 -81.8059 -255.639C-86.8656 -251.767 -87.5479 -244.534 -87.7825 -238.165C-92.67 -106.077 -116.983 60.2713 -104.453 191.861Z"
										fill="#B23A25"
									/>
								</g>
							</g>
						</g>
					</g>
					<defs>
						<clipPath id="clip0_19_463">
							<rect width="1738" height="637" fill="white" transform="matrix(1 0 0 -1 -108 317)" />
						</clipPath>
					</defs>
				</svg>
			) : (
				<svg
					viewBox="0 0 390 153"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className="h-[153px] w-full"
					preserveAspectRatio="none"
				>
					<path
						d="M-2.0616 134.823C19.0555 138.234 40.0214 128.042 57.4325 115.612C74.8436 103.182 90.6725 88.0855 110.191 79.3315C141.539 65.2722 178.122 69.5291 211.067 79.27C244.012 89.0108 275.44 103.917 309.07 110.936C340.028 117.396 372.4 119.727 403.376 112.092C431.651 105.124 458.703 92.5996 488.078 90.695C523.731 88.3837 559.387 93.2234 594.625 98.122C591.896 51.0462 587.873 4.04562 582.561 -42.8088C582.373 -44.4631 582.13 -46.2395 580.998 -47.4588C579.531 -49.0379 577.111 -49.1481 574.956 -49.1481C439.62 -49.1874 304.284 -49.2267 168.949 -49.2669C130.228 -49.278 91.3482 -49.2788 53.0259 -43.7376C33.0775 -40.853 11.4068 -35.2383 -0.0513744 -18.6532C-7.22597 -8.26889 -9.13965 4.77022 -10.5091 17.318C-13.2489 42.4238 -2.11884 109.568 -2.0616 134.823Z"
						fill="#F58968"
					/>
					<g style={{ mixBlendMode: 'multiply' }} opacity="0.2">
						<path
							d="M-2.0616 100.091C19.0555 103.502 40.0214 93.3097 57.4325 80.8798C74.8436 68.4499 90.6725 53.3533 110.191 44.5993C141.539 30.5399 178.122 34.7969 211.067 44.5378C244.012 54.2786 275.44 69.1847 309.07 76.2033C340.028 82.6639 372.4 84.9949 403.376 77.3602C431.651 70.3921 458.703 57.8674 488.078 55.9628C523.731 53.6515 559.387 58.4911 594.625 63.3898C591.896 16.314 587.873 -30.6866 582.561 -77.541C582.373 -79.1953 582.13 -80.9717 580.998 -82.191C579.531 -83.7701 577.111 -83.8803 574.956 -83.8803C439.62 -83.9196 304.284 -83.9589 168.949 -83.9991C130.228 -84.0102 91.3482 -84.0111 53.0259 -78.4699C33.0775 -75.5852 11.4068 -69.9705 -0.0513744 -53.3854C-7.22597 -43.0011 -9.13965 -29.962 -10.5091 -17.4142C-13.2489 7.69162 -2.11884 74.8353 -2.0616 100.091Z"
							fill="#E8876A"
						/>
					</g>
					<path
						d="M95.2437 -17.082C96.6336 2.98417 106.711 22.2993 122.375 34.9146C138.039 47.5299 159.056 53.2599 178.954 50.3385C191.331 48.5211 202.992 43.5848 214.77 39.3646C252.273 25.9265 293.099 19.5001 332.209 27.0757C413.528 42.8278 477.055 115.99 559.617 122.641C568.475 123.354 578.834 122.574 583.984 115.331C587.357 110.586 587.489 104.344 587.492 98.5225C587.513 61.4388 587.535 24.3543 587.556 -12.7294C587.558 -15.7781 587.474 -19.0849 585.578 -21.4723C583.052 -24.6543 578.41 -24.9388 574.348 -24.9568C416.318 -25.6258 253.222 -21.1416 95.2437 -17.082Z"
						fill="#EC5024"
					/>
					<g style={{ mixBlendMode: 'multiply' }} opacity="0.2">
						<path
							d="M123.607 -45.4502C124.997 -25.384 135.075 -6.06887 150.739 6.54642C166.403 19.1617 187.419 24.8917 207.317 21.9703C219.694 20.1529 231.356 15.2166 243.134 10.9965C280.637 -2.44169 321.462 -8.8681 360.573 -1.29242C441.892 14.4596 505.418 87.6221 587.981 94.2724C596.838 94.9859 607.198 94.2058 612.348 86.9625C615.72 82.2177 615.852 75.9758 615.855 70.1543C615.877 33.0706 615.898 -4.01389 615.92 -41.0976C615.921 -44.1463 615.837 -47.4531 613.942 -49.8404C611.416 -53.0225 606.774 -53.307 602.712 -53.325C444.682 -53.994 281.586 -49.5098 123.607 -45.4502Z"
							fill="#B23A25"
						/>
					</g>
					<path
						d="M-43.2068 152.548C-17.8642 155.188 8.45998 146.145 26.833 128.489C46.5815 109.51 57.5006 81.3797 81.0474 67.3888C96.0066 58.4998 114.16 56.6541 131.537 57.5453C148.914 58.4357 166.096 61.8373 183.479 62.585C198.963 63.2506 214.538 61.792 229.63 58.2631C249.577 53.5994 269.11 45.0035 283.515 30.4384C297.92 15.8723 306.532 -5.28842 302.803 -25.4341C302.412 -27.5412 301.857 -29.7013 300.507 -31.3649C297.684 -34.8434 292.53 -35.0152 288.053 -34.8938C182.355 -32.0399 76.6562 -29.1852 -29.0422 -26.3313C-30.8465 -26.2826 -32.7833 -26.1894 -34.2168 -25.0923C-36.2253 -23.5551 -36.4961 -20.6841 -36.5893 -18.1558C-38.5277 34.28 -48.179 100.314 -43.2051 152.55"
						fill="#EC5024"
					/>
					<g style={{ mixBlendMode: 'multiply' }} opacity="0.2">
						<path
							d="M-72.592 123.158C-47.2494 125.798 -20.9252 116.755 -2.55212 99.0988C17.1964 80.1194 28.1155 51.9897 51.6623 37.9987C66.6215 29.1097 84.7749 27.2641 102.152 28.1553C119.529 29.0456 136.711 32.4472 154.094 33.1949C169.578 33.8605 185.153 32.402 200.245 28.873C220.192 24.2094 239.725 15.6135 254.13 1.04829C268.535 -13.5177 277.147 -34.6785 273.418 -54.8242C273.027 -56.9313 272.472 -59.0914 271.122 -60.755C268.299 -64.2335 263.145 -64.4053 258.668 -64.2839C152.969 -61.43 47.2711 -58.5753 -58.4273 -55.7213C-60.2317 -55.6727 -62.1684 -55.5795 -63.602 -54.4824C-65.6105 -52.9452 -65.8813 -50.0742 -65.9744 -47.5459C-67.9146 4.88826 -77.5658 70.922 -72.592 123.158Z"
							fill="#B23A25"
						/>
					</g>
				</svg>
			)}
		</div>
	) : null
}
